import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { useFormatDateHook, useQueryHook } from "hooks";
import { useDealActions } from "store/actions-context";
import { SecurityTransaction } from "store/types";

const cellSx = {
  fontSize: "14px",
  fontWeight: 500,
  color: "#071F35",
  borderBottom: "1px solid  #DBE8F5"
};

export interface SecurityTransactionsProps {
  onCancel: () => void;
  deal_id: number;
  securityId: number | null;
  name: string;
  investmentEntity: string;
  institute: string;
}

export const SecurityTransactions: React.FC<SecurityTransactionsProps> = ({
  onCancel,
  deal_id,
  securityId = null,
  name,
  investmentEntity,
  institute
}) => {
  const { getSecurityTransactionsAction, getDealTransactionsAction } = useDealActions();
  const formatDate = useFormatDateHook();

  const { data: transactions, loading } = useQueryHook<SecurityTransaction[]>(() =>
    securityId === null ? getDealTransactionsAction(deal_id) : getSecurityTransactionsAction(securityId)
  );

  return (
    <Stack
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        padding: "20px",
        width: "900px",
        minHeight: "400px"
      }}
    >
      <AsyncContentMui isLoading={loading}>
        <Stack sx={{ padding: "12px 12px 16px 12px" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ borderBottom: "1px solid  #DBE8F5", paddingBottom: "5px" }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 400, color: "#071F35" }}>Security transactions</Typography>
            <IconButton onClick={onCancel} sx={{ p: 0 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
        </Stack>
        <Stack sx={{ p: 3, pt: 2 }}>
          <Stack direction="row" padding="0px 10px 10px 10px" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#071F35" }}>Security:</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#071F35" }}>{name}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#071F35" }}>Investment Entity:</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#071F35" }}>{investmentEntity}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#071F35" }}>Institute:</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#071F35" }}>{institute}</Typography>
            </Stack>
          </Stack>
          {!transactions || transactions.length === 0 ? (
            <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#071F35", textAlign: "center", py: 4 }}>
              There are no transaction data to show
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: "400px", overflow: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {!securityId && (
                      <TableCell
                        sx={{ fontSize: "14px", fontWeight: 500, color: "#071F35", backgroundColor: "#F6F8FC" }}
                      >
                        Account Details
                      </TableCell>
                    )}
                    <TableCell sx={{ fontSize: "14px", fontWeight: 500, color: "#071F35", backgroundColor: "#F6F8FC" }}>
                      Quantity
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px", fontWeight: 500, color: "#071F35", backgroundColor: "#F6F8FC" }}>
                      Effective date
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px", fontWeight: 500, color: "#071F35", backgroundColor: "#F6F8FC" }}>
                      Price per unit
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px", fontWeight: 500, color: "#071F35", backgroundColor: "#F6F8FC" }}>
                      Total
                    </TableCell>
                    <TableCell sx={{ fontSize: "14px", fontWeight: 500, color: "#071F35", backgroundColor: "#F6F8FC" }}>
                      Transaction Type
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction, index) => (
                    <TableRow key={index}>
                      {!securityId && (
                        <TableCell
                          sx={cellSx}
                        >{`${transaction.bank_name} / ${transaction.bank_account_number} / ${transaction.bank_branch_number}`}</TableCell>
                      )}
                      <TableCell sx={cellSx}>{transaction.quantity}</TableCell>
                      <TableCell sx={cellSx}>{formatDate({ date: transaction.date })}</TableCell>
                      <TableCell sx={cellSx}>{transaction.price}</TableCell>
                      <TableCell sx={cellSx}>{transaction.total}</TableCell>
                      <TableCell sx={cellSx}>{transaction.type}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
        <Stack sx={{ display: "flex", alignItems: "flex-end" }}>
          <Button variant="outlined" sx={{ width: "100px" }} onClick={onCancel}>
            OK
          </Button>
        </Stack>
      </AsyncContentMui>
    </Stack>
  );
};
